import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Policy, Provisioning } from '@wsphere/warranties/domain';
import { Subject, firstValueFrom, map, takeUntil, tap } from 'rxjs';
import { EvaluatedAddon } from '../../../policy';
import { ProvisioningSessionService } from '../../services';

interface PlanInfo {
  termId: string | null;
  plan: Policy.Plan.Model | null;
  addonIds: string[] | null;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-provision-plan',
  templateUrl: './provision-plan.component.html',
  standalone: false,
})
export class ProvisionPlanComponent implements OnInit, OnDestroy {
  @HostBinding('class') _class = 'pb-24 px-6';
  private sessionSvc = inject(ProvisioningSessionService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);

  protected stepCompleted = false;
  protected destroy$ = new Subject<void>();
  private latestTermUpdateToken = 0;
  private pendingTermUpdate: Promise<void> = Promise.resolve();

  isLargeScreen$ = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(map(state => !state.matches));

  protected session$ = this.sessionSvc.getCurrentSession().pipe(
    tap(session => {
      // Hydrate from session once per page entry. Do not overwrite after the user
      // starts interacting, otherwise a stale async session emission can wipe
      // newly selected addons/plan choices in the form.
      if (session?.plan && session.policyTermId && !this.plan.value?.plan?.id) {
        this.plan.patchValue({
          plan: session.plan,
          termId: session.policyTermId,
          addonIds: session.addonIds ?? null,
        });
        this.stepCompleted = true;
        // Re-sync the per-plan quote map with the authoritative session value so a markup edited
        // later (Add-on/Checkout steps) is reflected if the seller navigates back to this step.
        this.sessionSvc.setPlanMarkup(session.policyTermId, session.plan.id, session.markup ?? 0);
      }
    })
  );

  protected plan = new FormControl(null as PlanInfo | null);

  readonly staffView$ = this.route.data.pipe(map(data => data['staffView'] as boolean));

  protected hideMarkupEntry$ = this.sessionSvc.hideMarkupEntry$;

  protected setHideMarkupEntry(hide: boolean) {
    this.sessionSvc.hideMarkupEntry$.next(hide);
  }

  /** Per-policy kill switch (`Policy.allowMarkup`) — mirrors the gate already used at checkout. */
  protected markupAllowed(policy?: Policy.Model | null): boolean {
    return Policy.isMarkupAllowed(policy);
  }

  protected navigateTo(path?: string) {
    this.router.navigate([path ?? `..`], {
      relativeTo: this.route,
    });
  }

  previousPage(staffView: boolean) {
    if (staffView) this.navigateTo('/provisioning/asset');
    else this.navigateTo('/checkout/asset');
  }

  async nextPage(session: Provisioning.ProvisioningSession | null, staffView: boolean, selectedPlan?: PlanInfo | null) {
    const planSelection = selectedPlan ?? this.plan.value;

    if (this.stepCompleted && session && planSelection) {
      // Ensure any in-flight term-only update completes first so a stale response
      // cannot overwrite the plan/addon selection we are about to persist.
      await this.pendingTermUpdate;

      // Carry the quote entered for this specific (term, plan) card into the session as the
      // starting markup — admin dashboard only, mirroring the existing staffView-only gate.
      const markup =
        staffView && planSelection.termId && planSelection.plan?.id
          ? this.sessionSvc.getPlanMarkup(planSelection.termId, planSelection.plan.id)
          : undefined;

      await this.sessionSvc.updateSession({
        sessionId: session.id,
        data: {
          policyTermId: planSelection.termId || '',
          planId: planSelection.plan?.id,
          addonIds: planSelection.addonIds ?? [],
          ...(markup !== undefined ? { markup } : {}),
        },
      });

      const addons = this.sessionSvc.addons$.value;
      const hasAddons = addons?.find(
        addon => addon.planId === planSelection.plan?.id && addon.termId === planSelection.termId
      )?.addons.length;

      if (staffView) {
        if (!hasAddons) this.navigateTo('/provisioning/checkout');
        else this.navigateTo('/provisioning/addon');
      } else {
        if (!hasAddons) this.navigateTo('/checkout/checkout');
        else this.navigateTo('/checkout/addon');
      }
    }
  }

  emitAddons(addons: { planId: string; termId: string; addons: EvaluatedAddon[] }[] | null) {
    this.sessionSvc.addons$.next(addons);
  }

  async updateTerm(termId: string) {
    const session = await firstValueFrom(this.session$);
    if (!session || session.policyTermId === termId) return;

    const token = ++this.latestTermUpdateToken;

    const queuedUpdate = this.pendingTermUpdate
      .catch(() => undefined)
      .then(async () => {
        if (token !== this.latestTermUpdateToken) return;

        await this.sessionSvc.updateSession({ sessionId: session.id, data: { policyTermId: termId } });
      });

    // Keep the internal queue healthy after transient failures; callers of this
    // invocation still receive `queuedUpdate` errors via the await below.
    this.pendingTermUpdate = queuedUpdate.catch(() => undefined);

    await queuedUpdate;
  }

  ngOnInit(): void {
    this.plan.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(plan => (this.stepCompleted = !!plan?.plan?.id));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
